import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { aiVisibilityService } from '../../services/aiVisibilityService';
import StatusScreen, { StatusText } from '../../components/StatusScreen';
import { CONTACT_EMAIL, contactHref } from '../../utils/contact';

// Where Stripe sends the customer after paying (/pago/ok?session_id=…).
// Confirms the payment with the server, which starts the report if the
// Stripe webhook hasn't yet, and goes on to /informe/<id>. The session id
// stays in that address (?pago=…) so the report page can retry a failed
// report for this same payment.

const PROCESSING_POLL_MS = 5000;
const PROCESSING_MAX_POLLS = 24; // two minutes for a slow payment method
const ERROR_RETRIES = 2;

const SCREENS = {
  confirming: { title: 'Confirmando tu pago…', busy: true },
  processing: { title: 'Tu banco está confirmando el pago', busy: true },
  unpaid: { title: 'El pago no se ha completado' },
  exhausted: { title: 'No hemos podido generar tu informe' },
  unknown: { title: 'No encontramos este pago' },
  error: { title: 'No hemos podido confirmar el pago' }
};

const PaymentReturn = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'confirming', message: null });

  useEffect(() => {
    let cancelled = false;
    let timer;
    let polls = 0;
    let errors = 0;

    const confirm = async () => {
      const result = await aiVisibilityService.confirmPayment(sessionId);
      if (cancelled) return;
      if (result.success) {
        navigate(`/informe/${result.reportId}?pago=${encodeURIComponent(sessionId)}`, { replace: true });
        return;
      }
      if (result.status === 'processing' && polls < PROCESSING_MAX_POLLS) {
        polls += 1;
        setState({ status: 'processing', message: result.message });
        timer = setTimeout(confirm, PROCESSING_POLL_MS);
        return;
      }
      if (result.status === 'error' && errors < ERROR_RETRIES) {
        errors += 1;
        timer = setTimeout(confirm, 3000);
        return;
      }
      setState({ status: SCREENS[result.status] ? result.status : 'error', message: result.message });
    };

    setState({ status: 'confirming', message: null });
    if (sessionId) confirm();
    else setState({ status: 'unknown', message: 'Falta la referencia del pago en el enlace.' });

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId, navigate, attempt]);

  const screen = SCREENS[state.status];
  const mailto = contactHref('Mi informe de webspector', `Referencia del pago: ${sessionId || '(sin referencia)'}`);
  const needsHelp = ['exhausted', 'unknown', 'error'].includes(state.status);

  return (
    <StatusScreen kicker="Pago del informe completo" title={screen.title} busy={screen.busy}>
      {state.message && <StatusText>{state.message}</StatusText>}
      {needsHelp && mailto && (
        <StatusText>
          Escríbenos a{' '}
          <a href={mailto} className="text-[var(--lp-text)] underline underline-offset-4">
            {CONTACT_EMAIL}
          </a>{' '}
          con esta referencia y lo resolvemos.
        </StatusText>
      )}
      <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-5">
        {state.status === 'error' && (
          <button type="button" className="lp-btn" onClick={() => setAttempt((n) => n + 1)}>
            Volver a intentarlo
          </button>
        )}
        {!screen.busy && (
          <Link to="/" className="font-semibold underline decoration-[var(--lp-lime)] decoration-2 underline-offset-8">
            Volver al inicio
          </Link>
        )}
      </div>
    </StatusScreen>
  );
};

export default PaymentReturn;
